import dayjs from 'dayjs'

/**
 * 获取去年同期日期
 * @param {number|string} date - 日期，格式为 YYYYMMDD 的数字或字符串
 * @returns {number} 去年同期日期，格式为 YYYYMMDD 的数字
 */
export const getYearOverYearDate = (date) => {
  const dateStr = String(date)
  const year = parseInt(dateStr.slice(0, 4), 10)
  const month = parseInt(dateStr.slice(4, 6), 10)
  const day = parseInt(dateStr.slice(6, 8), 10)

  // 处理2月29日的情况：如果去年不是闰年，使用2月28日
  let targetYear = year - 1
  let targetMonth = month
  let targetDay = day

  if (month === 2 && day === 29) {
    // 检查去年是否是闰年
    const lastYear = new Date(targetYear, 1, 29)
    if (lastYear.getDate() !== 29) {
      // 去年不是闰年，使用2月28日
      targetDay = 28
    }
  }

  const targetDate = dayjs(`${targetYear}-${String(targetMonth).padStart(2, '0')}-${String(targetDay).padStart(2, '0')}`)
  return Number(targetDate.format('YYYYMMDD'))
}

/**
 * 获取上月同期日期
 * @param {number|string} date - 日期，格式为 YYYYMMDD 的数字或字符串
 * @returns {number} 上月同期日期，格式为 YYYYMMDD 的数字
 */
export const getMonthOverMonthDate = (date) => {
  const dateStr = String(date)
  const year = parseInt(dateStr.slice(0, 4), 10)
  const month = parseInt(dateStr.slice(4, 6), 10)
  const day = parseInt(dateStr.slice(6, 8), 10)

  let targetYear = year
  let targetMonth = month - 1
  let targetDay = day

  // 处理跨年的情况
  if (targetMonth < 1) {
    targetMonth = 12
    targetYear = year - 1
  }

  // 处理不同月份天数的情况
  // 例如：3月31日 -> 2月28/29日
  const daysInTargetMonth = dayjs(`${targetYear}-${String(targetMonth).padStart(2, '0')}-01`).daysInMonth()
  if (day > daysInTargetMonth) {
    targetDay = daysInTargetMonth
  }

  const targetDate = dayjs(`${targetYear}-${String(targetMonth).padStart(2, '0')}-${String(targetDay).padStart(2, '0')}`)
  return Number(targetDate.format('YYYYMMDD'))
}

/**
 * 获取自定义对比日期
 * @param {number|string} date - 当前日期，格式为 YYYYMMDD 的数字或字符串
 * @param {number} year - 对比年份
 * @param {number} month - 对比月份（1-12）
 * @returns {number} 自定义对比日期，格式为 YYYYMMDD 的数字
 */
export const getCustomContrastDate = (date, year, month) => {
  const dateStr = String(date)
  const day = parseInt(dateStr.slice(6, 8), 10)

  // 处理不同月份天数的情况
  const daysInTargetMonth = dayjs(`${year}-${String(month).padStart(2, '0')}-01`).daysInMonth()
  let targetDay = day
  if (day > daysInTargetMonth) {
    targetDay = daysInTargetMonth
  }

  const targetDate = dayjs(`${year}-${String(month).padStart(2, '0')}-${String(targetDay).padStart(2, '0')}`)
  return Number(targetDate.format('YYYYMMDD'))
}

/**
 * 根据对比类型获取单个日期的对比日期
 * @param {number|string} date - 当前日期，格式为 YYYYMMDD 的数字或字符串
 * @param {string} contrastType - 对比类型：'yoy' | 'mom' | 'custom'
 * @param {number} customYear - 自定义年份（仅当contrastType为'custom'时使用）
 * @param {number} customMonth - 自定义月份（仅当contrastType为'custom'时使用）
 * @returns {number} 对比日期，格式为 YYYYMMDD 的数字
 */
export const getContrastDate = (date, contrastType, customYear = null, customMonth = null) => {
  if (!contrastType || contrastType === 'off') {
    return null
  }

  switch (contrastType) {
    case 'yoy':
      return getYearOverYearDate(date)
    case 'mom':
      return getMonthOverMonthDate(date)
    case 'custom':
      if (customYear && customMonth) {
        return getCustomContrastDate(date, customYear, customMonth)
      }
      return null
    default:
      return null
  }
}

/**
 * 根据对比类型计算对比日期范围
 * @param {number|string} startDate - 开始日期，格式为 YYYYMMDD 的数字或字符串
 * @param {number|string} endDate - 结束日期，格式为 YYYYMMDD 的数字或字符串
 * @param {string} contrastType - 对比类型：'yoy' | 'mom' | 'custom'
 * @param {number} customYear - 自定义年份（仅当contrastType为'custom'时使用）
 * @param {number} customMonth - 自定义月份（仅当contrastType为'custom'时使用）
 * @returns {{startDate: number, endDate: number}} 对比日期范围对象
 */
export const getContrastDateRange = (startDate, endDate, contrastType, customYear = null, customMonth = null) => {
  if (!contrastType || contrastType === 'off') {
    return { startDate: null, endDate: null }
  }

  // 计算当前日期范围的长度（天数）
  const currentStartDateObj = dayjs(String(startDate), 'YYYYMMDD')
  const currentEndDateObj = dayjs(String(endDate), 'YYYYMMDD')
  const dateRangeLength = currentEndDateObj.diff(currentStartDateObj, 'day')

  let contrastStartDate, contrastEndDate

  switch (contrastType) {
    case 'yoy': {
      // 年对比：先根据 endDate 减去1年得到对比的 endDate，然后往前推相同的天数得到 startDate
      const contrastEndDateObj = currentEndDateObj.subtract(1, 'year')
      // 处理2月29日的情况
      const contrastEndDateStr = String(Number(contrastEndDateObj.format('YYYYMMDD')))
      const contrastEndYear = parseInt(contrastEndDateStr.slice(0, 4), 10)
      const contrastEndMonth = parseInt(contrastEndDateStr.slice(4, 6), 10)
      const contrastEndDay = parseInt(contrastEndDateStr.slice(6, 8), 10)
      
      // 如果原日期是2月29日，但对比年份不是闰年，使用2月28日
      if (currentEndDateObj.month() === 1 && currentEndDateObj.date() === 29) {
        const lastYearFeb29 = new Date(contrastEndYear, 1, 29)
        if (lastYearFeb29.getDate() !== 29) {
          contrastEndDate = Number(dayjs(`${contrastEndYear}-02-28`).format('YYYYMMDD'))
        } else {
          contrastEndDate = Number(contrastEndDateObj.format('YYYYMMDD'))
        }
      } else {
        contrastEndDate = Number(contrastEndDateObj.format('YYYYMMDD'))
      }
      
      // 从对比的 endDate 往前推相同的天数得到 startDate
      const contrastStartDateObj = dayjs(String(contrastEndDate), 'YYYYMMDD').subtract(dateRangeLength, 'day')
      contrastStartDate = Number(contrastStartDateObj.format('YYYYMMDD'))
      break
    }
    case 'mom': {
      // 月对比：先根据 endDate 减去1个月得到对比的 endDate，然后往前推相同的天数得到 startDate
      const contrastEndDateObj = currentEndDateObj.subtract(1, 'month')
      // 处理不同月份天数的情况（例如：3月31日 -> 2月28/29日）
      const daysInTargetMonth = contrastEndDateObj.daysInMonth()
      const originalDay = currentEndDateObj.date()
      const adjustedDay = Math.min(originalDay, daysInTargetMonth)
      
      const contrastEndDateAdjusted = dayjs(`${contrastEndDateObj.year()}-${String(contrastEndDateObj.month() + 1).padStart(2, '0')}-${String(adjustedDay).padStart(2, '0')}`)
      contrastEndDate = Number(contrastEndDateAdjusted.format('YYYYMMDD'))
      
      // 从对比的 endDate 往前推相同的天数得到 startDate
      const contrastStartDateObj = dayjs(String(contrastEndDate), 'YYYYMMDD').subtract(dateRangeLength, 'day')
      contrastStartDate = Number(contrastStartDateObj.format('YYYYMMDD'))
      break
    }
    case 'custom': {
      if (!customYear || !customMonth) {
        return { startDate: null, endDate: null }
      }
      // 自定义：主选择的 end_date 应该跟需要对比的 end_date 在每月的 day 上做对应
      // 例如：当前数据是 2025-10-30 到 2025-12-29，选择 2024年10月份
      // 对比的 end_date 应该是 2024-10-29（对应原 end_date 的 day 29）
      // 然后从对比的 end_date 往前推相同的天数得到 start_date
      const originalEndDay = currentEndDateObj.date()
      const targetMonthObj = dayjs(`${customYear}-${String(customMonth).padStart(2, '0')}-01`)
      const daysInTargetMonth = targetMonthObj.daysInMonth()
      // 如果原 end_date 的 day 超过目标月份的最大天数，使用目标月份的最大天数
      const targetDay = Math.min(originalEndDay, daysInTargetMonth)
      
      const contrastEndDateObj = dayjs(`${customYear}-${String(customMonth).padStart(2, '0')}-${String(targetDay).padStart(2, '0')}`)
      contrastEndDate = Number(contrastEndDateObj.format('YYYYMMDD'))
      
      // 从对比的 endDate 往前推相同的天数得到 startDate
      const contrastStartDateObj = contrastEndDateObj.subtract(dateRangeLength, 'day')
      contrastStartDate = Number(contrastStartDateObj.format('YYYYMMDD'))
      break
    }
    default:
      return { startDate: null, endDate: null }
  }

  return {
    startDate: contrastStartDate,
    endDate: contrastEndDate,
  }
}

